import { useEffect, useRef, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2, GitBranch, Maximize2, Minimize2 } from 'lucide-react'

interface WorkflowVisualizerProps {
  projectId?: string
}

// 安全说明：mermaid 从 npm 依赖动态 import（打包进 chunk），
// 不再运行时从 jsdelivr CDN 加载脚本（CDN 劫持 = 全站 XSS，且被 CSP script-src 'self' 阻断）
// mermaid 模块较大，动态 import 让其按需加载，不拖累首屏
let mermaidModulePromise: Promise<any> | null = null

/**
 * 懒加载 mermaid 模块（单例，避免重复加载）
 */
async function loadMermaid(): Promise<any> {
  if (!mermaidModulePromise) {
    mermaidModulePromise = import('mermaid').then((mod) => {
      // mermaid 11 为 ESM-only，default 导出存在；加兜底兼容命名导出
      const mermaid = (mod as any).default ?? (mod as any)
      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables: {
          primaryColor: '#3b82f6',
          primaryTextColor: '#1f2937',
          primaryBorderColor: '#2563eb',
          lineColor: '#6b7280',
          secondaryColor: '#f3f4f6',
          tertiaryColor: '#eff6ff',
        },
      })
      return mermaid
    })
  }
  return mermaidModulePromise
}

const defaultMermaid = `
graph TD
    A[开始] --> B[文档分析]
    B --> C[术语识别]
    C --> D[初始翻译]
    D --> E{自省检查}
    E -->|需要改进| F[优化翻译]
    F --> E
    E -->|通过| G[交付]
    G --> H[结束]

    style A fill:#e0f2fe
    style H fill:#dcfce7
    style E fill:#fef3c7
    style G fill:#dbeafe
`

/**
 * 工作流可视化组件
 * 使用Mermaid展示翻译工作流程图
 *
 * 注意：不再直连 Python Engine 的 /translate/visualize 端点——
 * 该端点有 token 保护，浏览器没有 token（线上必 401），
 * 且直连会把引擎内部地址暴露进前端 bundle。统一使用内置默认流程图。
 */
export function WorkflowVisualizer({ projectId: _projectId }: WorkflowVisualizerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [mermaidLoaded, setMermaidLoaded] = useState(false)

  useEffect(() => {
    let cancelled = false
    loadMermaid().then(() => {
      if (!cancelled) setMermaidLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current || !mermaidLoaded) return

      const mermaid = await loadMermaid()
      if (!mermaid) return

      const mermaidStr = defaultMermaid

      try {
        containerRef.current.innerHTML = ''
        const { svg } = await mermaid.render('workflow-diagram', mermaidStr)
        containerRef.current.innerHTML = svg
      } catch (error) {
        console.error('Mermaid render error:', error)
        containerRef.current.innerHTML = `<pre class="text-xs text-muted-foreground p-4 bg-muted rounded">${mermaidStr}</pre>`
      }
    }

    renderDiagram()
  }, [mermaidLoaded])

  return (
    <Card className={`${isFullscreen ? 'fixed inset-4 z-50' : ''}`}>
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-primary" />
          <CardTitle className="text-sm font-medium">翻译工作流</CardTitle>
          <Badge variant="outline" className="text-[10px] h-5">
            LangGraph
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => setIsFullscreen(!isFullscreen)}
          >
            {isFullscreen ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        {!mermaidLoaded ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div
            ref={containerRef}
            className="overflow-auto max-h-[400px] flex justify-center"
          />
        )}
        
        <div className="mt-4 pt-4 border-t">
          <div className="grid grid-cols-3 gap-2 text-[10px]">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-info/15 border border-info/40" />
              <span className="text-muted-foreground">开始/结束节点</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-warning/15 border border-warning/40" />
              <span className="text-muted-foreground">决策节点</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded bg-muted border border-border" />
              <span className="text-muted-foreground">处理节点</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
